import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Progress,
  message,
  Spin,
} from "antd";
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  ShopOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";
import DashboardCards from "../Cards/DashboardCards";

const { Title, Text } = Typography;

const ProfitCards = ({ isDark }) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);

  // ---------------- FETCH PROFIT ----------------
  const fetchProfit = async () => {
    setLoading(true);
    try {
      const res = await api.get("profit/");

      setProducts(res.data.products || []);
      setTotalProfit(Number(res.data.total_profit || 0));
    } catch (err) {
      message.error("Failed to load profit data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfit();
  }, []);

  // ---------------- FORMAT ----------------
  const format = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ---------------- BACKEND-BASED KPI LOGIC ----------------

  const totalProducts = products.length;

  const totalQuantitySold = products.reduce(
    (sum, p) => sum + Number(p.quantity_sold || 0),
    0
  );

  const totalWholesaleCost = products.reduce(
    (sum, p) =>
      sum + Number(p.wholesale_price || 0) * Number(p.quantity_sold || 0),
    0
  );

  const totalRevenue = products.reduce(
    (sum, p) =>
      sum + Number(p.selling_price || 0) * Number(p.quantity_sold || 0),
    0
  );

  const lossProducts = products.filter(
    (p) => Number(p.total_profit || 0) < 0
  ).length;

  // ---------------- UI ----------------
  return (
    <div>
      <Title
        level={3}
        style={{
          color: isDark ? "#f9fafb" : "#111827",
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        Profit Dashboard Overview
      </Title>

      <Text style={{ color: isDark ? "#d1d5db" : "#4b5563", fontSize: 14 }}>
        Real-time profit, sales, and product performance analytics
      </Text>

      <Divider />

      {loading && <Spin style={{ marginBottom: 15 }} />}

      {/* KPI CARDS */}
      <Row gutter={[16, 16]}>
        {/* NET PROFIT */}
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle(isDark)}>
            <DollarOutlined style={{ fontSize: 28, color: "#52c41a" }} />

            <Title level={3} style={titleStyle(isDark)}>
              ${format(totalProfit)}
            </Title>

            <Text style={textStyle(isDark)}>Net Profit</Text>
          </Card>
        </Col>

        {/* PRODUCTS */}
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle(isDark)}>
            <RiseOutlined style={{ fontSize: 28, color: "#1890ff" }} />

            <Title level={3} style={titleStyle(isDark)}>
              {totalProducts}
            </Title>

            <Text style={textStyle(isDark)}>Total Products</Text>
          </Card>
        </Col>

        {/* SOLD */}
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle(isDark)}>
            <ShopOutlined style={{ fontSize: 28, color: "#faad14" }} />

            <Title level={3} style={titleStyle(isDark)}>
              {totalQuantitySold}
            </Title>

            <Text style={textStyle(isDark)}>Items Sold</Text>
          </Card>
        </Col>

        {/* LOSS */}
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle(isDark)}>
            <FallOutlined style={{ fontSize: 28, color: "#ff4d4f" }} />

            <Title level={3} style={titleStyle(isDark)}>
              {lossProducts}
            </Title>

            <Text style={textStyle(isDark)}>Loss Products</Text>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* ANALYTICS */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Business Performance" style={cardStyle(isDark)}>
            <p style={textStyle(isDark)}>Profit Margin</p>
            <Progress percent={totalRevenue ? (totalProfit / totalRevenue) * 100 : 0} />

            <p style={textStyle(isDark)}>Revenue Performance</p>
            <Progress percent={75} />

            <p style={{ ...textStyle(isDark), color: "#ff4d4f" }}>
              Cost Pressure
            </p>
            <Progress percent={40} status="exception" />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Executive Insights" style={cardStyle(isDark)}>
            <p style={textStyle(isDark)}>• Total profit: ${format(totalProfit)}</p>
            <p style={textStyle(isDark)}>• Products: {totalProducts}</p>
            <p style={textStyle(isDark)}>• Items sold: {totalQuantitySold}</p>
            <p style={textStyle(isDark)}>• Loss products: {lossProducts}</p>
          </Card>
        </Col>
      </Row>

      <br />

      <DashboardCards />
    </div>
  );
};

// ---------------- STYLES ----------------
const cardStyle = (isDark) => ({
  borderRadius: 12,
  background: isDark ? "#111827" : "#ffffff",
  boxShadow: isDark ? "none" : "0 4px 12px rgba(0,0,0,0.06)",
});

const titleStyle = (isDark) => ({
  marginTop: 10,
  color: isDark ? "#fff" : "#111827",
});

const textStyle = (isDark) => ({
  color: isDark ? "#d1d5db" : "#6b7280",
  fontSize: 14,
});

export default ProfitCards;